import { useMemo, lazy, Suspense } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  ClipboardList,
  Calendar,
  FolderKanban,
  BarChart3,
  HelpCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  Star,
  CheckSquare,
  FileText,
  Check,
  MessageCircle,
  Sparkles,
  Plus,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Tooltip } from '@/components/ui'
import { useUIStore } from '@/stores/uiStore'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { formatDate } from '@/utils/format'
import { getCategoryBgStyle, getCategoryColorStyle } from '@/lib/categoryColors'

const MiniCalendar = lazy(() =>
  import('@/components/calendar/MiniCalendar').then((m) => ({ default: m.MiniCalendar }))
)

type NavItemType = 'link' | 'modal'

interface NavItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  type: NavItemType
  path?: string
}

const navItems: NavItem[] = [
  { id: 'tasks', label: '작업', icon: ClipboardList, type: 'link', path: '/' },
  { id: 'chat', label: '채팅', icon: MessageCircle, type: 'link', path: '/chat' },
  { id: 'calendar', label: '캘린더', icon: Calendar, type: 'link', path: '/calendar' },
  { id: 'groups', label: '그룹', icon: FolderKanban, type: 'link', path: '/groups' },
  { id: 'ai', label: 'AI 코치', icon: Sparkles, type: 'link', path: '/ai' },
  { id: 'profile', label: '분석', icon: BarChart3, type: 'link', path: '/profile' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, type: 'modal' },
  { id: 'terms', label: '서비스 약관', icon: FileText, type: 'modal' },
  { id: 'settings', label: '설정', icon: Settings, type: 'modal' },
]

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const openTaskCreateModal = useUIStore((state) => state.openTaskCreateModal)
  const openFAQModal = useUIStore((state) => state.openFAQModal)
  const openTermsModal = useUIStore((state) => state.openTermsModal)
  const openSettingsModal = useUIStore((state) => state.openSettingsModal)
  const setActiveCategoryTab = useUIStore((state) => state.setActiveCategoryTab)
  const calendarSelectedDate = useUIStore((state) => state.calendarSelectedDate)
  const setCalendarSelectedDate = useUIStore((state) => state.setCalendarSelectedDate)
  const tasks = useTaskStore((state) => state.tasks)

  // M1: Memoize derived task lists
  const starredTasks = useMemo(() =>
    tasks.filter((t) => t.isStarred && t.status === 'pending'),
    [tasks]
  )

  const today = formatDate(new Date())
  const todayTasks = useMemo(() => tasks.filter(t => t.dueDate === today), [tasks, today])
  const todayCompleted = useMemo(() => todayTasks.filter(t => t.status === 'completed').length, [todayTasks])
  const todayPending = useMemo(() => todayTasks.filter(t => t.status === 'pending').length, [todayTasks])

  // Recent completed tasks
  const recentCompleted = useMemo(() => {
    return tasks
      .filter(t => t.status === 'completed' && t.updatedAt)
      .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
      .slice(0, 3)
  }, [tasks])

  // Category quick filters
  const categories = useCategoryStore((s) => s.categories)
  const rootCategories = useMemo(() =>
    categories.filter(c => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder),
    [categories]
  )

  const handleNavClick = (item: NavItem) => {
    if (item.type === 'modal') {
      switch (item.id) {
        case 'faq':
          openFAQModal()
          break
        case 'terms':
          openTermsModal()
          break
        case 'settings':
          openSettingsModal()
          break
      }
    }
  }

  const isActive = (item: NavItem) => {
    if (item.type === 'link' && item.path) {
      if (item.path === '/') {
        return location.pathname === '/' || location.pathname === '/tasks'
      }
      if (item.path === '/groups') {
        return location.pathname.startsWith('/groups')
      }
      if (item.path === '/ai') {
        return location.pathname.startsWith('/ai')
      }
      return location.pathname === item.path
    }
    return false
  }

  return (
    <aside
      className={clsx(
        'workspace-sidebar hidden lg:flex flex-col fixed h-[100dvh] border-r transition-[width] duration-300 z-40 el-sidebar-edge', /* --z-sidebar + Edge Lighting */
        isSidebarOpen ? 'w-[280px]' : 'w-20'
      )}
      role="complementary"
      aria-label="사이드 네비게이션"
      aria-expanded={isSidebarOpen}
    >
      {/* Logo */}
      <div className="flex items-center min-h-[72px] px-5 border-b border-[var(--workspace-border)]">
        <Link
          to="/"
          className="flex items-center gap-3 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
          aria-label="작업 목록으로 이동"
        >
          <div className="workspace-brand-mark w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0">
            <CheckSquare className="w-5 h-5 text-white" aria-hidden="true" />
          </div>
          {isSidebarOpen && (
            <div className="flex flex-col min-w-0">
              <span className="font-bold tracking-[-0.02em] text-heading truncate">
                Moonwave
              </span>
              <span className="text-meta text-caption truncate">나만의 집중 공간</span>
            </div>
          )}
        </Link>
      </div>

      {/* Add Task Button */}
      <div className="px-3 pt-4">
        {isSidebarOpen ? (
          <button
            onClick={openTaskCreateModal}
            className="workspace-primary-action w-full min-h-[48px] flex items-center justify-center gap-2 px-4 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            aria-label="새 작업 추가"
          >
            <Plus className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <span>새 작업</span>
          </button>
        ) : (
          <Tooltip content="새 작업 추가 (Ctrl+N)" placement="right">
            <button
              onClick={openTaskCreateModal}
              className="workspace-primary-action w-full min-h-[48px] flex items-center justify-center rounded-xl text-white transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              aria-label="새 작업 추가"
            >
              <Plus className="w-5 h-5" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-5" aria-label="주 메뉴">
        {isSidebarOpen && (
          <p className="px-6 pb-2 text-meta font-semibold uppercase tracking-[0.16em] text-disabled">
            메뉴
          </p>
        )}
        <ul className="space-y-1 px-3" role="menubar">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item)

            const navButton = item.type === 'link' ? (
              <Link
                to={item.path!}
                className={clsx(
                  'w-full min-h-[44px] flex items-center gap-3 px-3 rounded-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  active
                    ? 'workspace-nav-active el-sidebar-active'
                    : 'text-body hover-surface el-hover'
                )}
                role="menuitem"
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {isSidebarOpen && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
              </Link>
            ) : (
              <button
                onClick={() => handleNavClick(item)}
                className={clsx(
                  'w-full min-h-[44px] flex items-center gap-3 px-3 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  'text-body hover-surface'
                )}
                role="menuitem"
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {isSidebarOpen && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
              </button>
            )

            return (
              <li key={item.id} role="none">
                {!isSidebarOpen ? (
                  <Tooltip content={item.label} placement="right">
                    {navButton}
                  </Tooltip>
                ) : (
                  navButton
                )}
              </li>
            )
          })}
        </ul>

        {/* Starred Tasks Section */}
        {isSidebarOpen && starredTasks.length > 0 && (
          <section className="mt-6 px-2" aria-labelledby="starred-heading">
            <h2
              id="starred-heading"
              className="px-3 mb-2 text-xs font-semibold text-caption uppercase tracking-wider"
            >
              별표 작업
            </h2>
            <ul className="space-y-0.5" role="menu">
              {starredTasks.slice(0, 5).map((task: typeof starredTasks[number]) => {
                const active = location.pathname === `/task/${task.id}`

                return (
                  <li key={task.id} role="none">
                    <Link
                      to={`/task/${task.id}`}
                      className={clsx(
                        'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                        active
                          ? 'bg-surface-tertiary text-heading'
                          : 'text-body hover-surface'
                      )}
                      role="menuitem"
                      aria-current={active ? 'page' : undefined}
                    >
                      <Star
                        className="w-4 h-4 flex-shrink-0 text-warning-500 fill-warning-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm truncate">{task.title}</span>
                    </Link>
                  </li>
                )
              })}
              {starredTasks.length > 5 && (
                <li role="none">
                  <button
                    type="button"
                    onClick={() => navigate('/?filter=starred')}
                    className="block px-3 py-1.5 text-xs text-accent-primary hover:underline"
                  >
                    모두 보기 ({starredTasks.length})
                  </button>
                </li>
              )}
            </ul>
          </section>
        )}

        {/* Collapsed starred indicator */}
        {!isSidebarOpen && starredTasks.length > 0 && (
          <div className="mt-6 px-2" aria-label={`별표 작업 ${starredTasks.length}개`}>
            <div className="flex justify-center">
              <Tooltip content={`별표 작업 ${starredTasks.length}개`} placement="right">
                <div className="w-8 h-8 rounded-lg bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center cursor-default">
                  <Star
                    className="w-4 h-4 text-warning-500 fill-warning-500"
                    aria-hidden="true"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Today's Summary Widget */}
        {isSidebarOpen && (
          <section className="workspace-panel mx-4 mt-5 p-4">
            <h2 className="text-meta font-semibold text-caption uppercase tracking-[0.14em] mb-2.5">오늘의 요약</h2>
            <div className="flex items-center gap-3 text-label">
              <span className="text-success-600 dark:text-success-400 font-medium">{todayCompleted} 완료</span>
              <span className="text-caption">·</span>
              <span className="text-warning-600 dark:text-warning-400 font-medium">{todayPending} 대기</span>
            </div>
            {todayTasks.length > 0 && (
              <div className="mt-1.5 h-1.5 bg-surface-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${todayTasks.length > 0 ? (todayCompleted / todayTasks.length) * 100 : 0}%` }}
                />
              </div>
            )}
          </section>
        )}

        {/* Recent Activity Widget */}
        {isSidebarOpen && recentCompleted.length > 0 && (
          <section className="mt-4 px-4">
            <h2 className="text-xs font-semibold text-caption uppercase tracking-wider mb-2">최근 완료</h2>
            <ul className="space-y-1">
              {recentCompleted.map(t => (
                <li key={t.id} className="flex items-center gap-2 text-sm text-disabled">
                  <Check className="w-3 h-3 text-success-500 flex-shrink-0" />
                  <span className="truncate">{t.title}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Category Quick Filter Widget */}
        {isSidebarOpen && rootCategories.length > 0 && (
          <section className="mt-4 px-4">
            <h2 className="text-xs font-semibold text-caption uppercase tracking-wider mb-2">카테고리</h2>
            <div className="flex flex-wrap gap-1.5">
              {rootCategories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => { setActiveCategoryTab(cat.id!); navigate('/') }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: getCategoryBgStyle(cat.color),
                    color: getCategoryColorStyle(cat.color),
                  }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getCategoryColorStyle(cat.color) }} />
                  {cat.name}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Mini Calendar */}
        {isSidebarOpen && (
          <section className="mt-4 px-4 pb-2" aria-label="미니 캘린더">
            <Suspense fallback={null}>
              <MiniCalendar
                selectedDate={calendarSelectedDate}
                onSelectDate={setCalendarSelectedDate}
              />
            </Suspense>
          </section>
        )}
      </nav>

      {/* Collapse Toggle */}
      <div className="p-3 border-t border-[var(--workspace-border)]">
        {!isSidebarOpen ? (
          <Tooltip content="사이드바 펼치기" placement="right">
            <button
              onClick={toggleSidebar}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] rounded-lg text-caption hover-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="사이드바 펼치기"
              aria-expanded={false}
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </Tooltip>
        ) : (
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] rounded-lg text-caption hover-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="사이드바 접기"
            aria-expanded={true}
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            <span className="text-sm">접기</span>
          </button>
        )}
      </div>
    </aside>
  )
}
